import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Lottie from 'react-lottie';
import { Button, Card } from '@blueprintjs/core';
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useMapEvents } from 'react-leaflet-rotate/react-leaflet';

import { Load as LoadingAnimation } from '../../Data/Lottie';
import api from '../../Services/node';
import './style.css';
import { Map, Opcoes } from '../Mapas/map_components';

// Importar as imagens para os botões de visualização
import FundoOpenStreet from '../../Assets/Images/MapasFundo/FundoOpenStreet.png';
import FundoSatelite from '../../Assets/Images/MapasFundo/FundoSatelite.jpg';

interface PublicMapParams {
  empresaMae?: string;
  obra?: string;
  token?: string;
}

const PublicMapView: React.FC = () => {
  // Estados para controlar o mapa
  const [listaDeMarcacoes, setListaDeMarcacoes] = useState<any>();
  const [tiposInadimplencia, setTiposInadimplencia] = useState<any>();
  const [listaAreas, setListaAreas] = useState<any>();
  const [visaoMapa, setVisaoMapa] = useState<number>(0);
  const [carregandoListaDeMarcacoes, setCarregandoListaDeMarcacoes] = useState<boolean>(true);
  const [mapRefreshed, setMapRefreshed] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshData, setRefreshData] = useState<any>();
  const [mapaInfo, setMapaInfo] = useState<{nome: string; empresa: string} | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tileLayer, setTileLayer] = useState<number>(0); // 0 = GPS, 1 = Satélite
  
  // Estados para controlar a legenda
  const [exibirLegenda, setExibirLegenda] = useState<boolean>(false);
  const [legendaOption, setLegendaOption] = useState<number>(0);
  
  // Estado para filtrar apenas unidades disponíveis
  const [mostrarApenasDisponiveis, setMostrarApenasDisponiveis] = useState<boolean>(false);
  
  // Dados para a legenda (similar à página original)
  const optionsToChoose = [
    { id: 0, name: "Legenda", width: 70 },
    { id: 1, name: "Filtro", width: 70 },
    { id: 2, name: "Visualização", width: 90 }
  ];
  
  const handle = useFullScreenHandle();

  // Obter os parâmetros da URL
  const { token } = useParams<PublicMapParams>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const empresaMae = queryParams.get('empresaMae') || undefined;
  const obra = queryParams.get('obra') || undefined;

  // Efeitos para carregar os dados do mapa
  useEffect(() => {
    async function loadMapData() {
      // Verificar qual formato de URL está sendo usado
      if (token) {
        // Formato antigo com token
        console.log('Iniciando carregamento do mapa com token:', token);
        
        try {
          // Chama o endpoint para obter dados do mapa baseado no token
          console.log('Fazendo requisição para:', `/public/map/${token}`);
          const response = await api.get(`/public/map/${token}`);
          console.log('Resposta da API:', response.data);
          
          if (response.data && response.data.success) {
            console.log('Dados do mapa recebidos com sucesso:', response.data.mapData);
            setListaDeMarcacoes(response.data.mapData);
            setTiposInadimplencia(response.data.mapData.tiposInadimplencia);
            setListaAreas(response.data.mapData.others);
            setMapaInfo({
              nome: response.data.mapData.nome,
              empresa: response.data.mapData.empresa
            });
            setCarregandoListaDeMarcacoes(false);
          } else {
            console.error('Resposta da API indica token inválido:', response.data);
            setError('Token inválido ou expirado');
            setCarregandoListaDeMarcacoes(false);
          }
        } catch (error: any) {
          console.error('Erro ao carregar dados do mapa com token:', error);
          setError('Erro ao carregar o mapa. Por favor, verifique o link ou tente novamente mais tarde.');
          setCarregandoListaDeMarcacoes(false);
        }
      } else if (empresaMae && obra) {
        // Formato novo com URL direta
        console.log('Iniciando carregamento do mapa com parâmetros diretos:', { empresaMae, obra });
        
        try {        
          // Chama o endpoint para obter dados do mapa diretamente
          console.log('Fazendo requisição para:', `/allMaps?empresaMae=${empresaMae}&obra=${obra}`);
          const response = await api.get(`/allMaps?empresaMae=${empresaMae}&obra=${obra}`);
          console.log('Resposta da API:', response.data);
          
          // A API /allMaps retorna os dados diretamente, sem wrapper de success
          if (response.data) {
            console.log('Dados do mapa recebidos com sucesso:', response.data);
            
            // Adaptar o formato da resposta para o formato esperado pelo componente
            const mapData = response.data;

            // Garantir que a propriedade 'center' exista para evitar o crash
            if (!mapData.center) {
              if (mapData.lista && mapData.lista.length > 0) {
                // Use o centro da primeira quadra se disponível
                if (mapData.lista[0].center) {
                  console.log('Usando o centro da primeira quadra:', mapData.lista[0].center);
                  mapData.center = mapData.lista[0].center;
                } 
                // Outra abordagem: tente usar o centro do primeiro lote
                else if (mapData.lista[0].lotes && mapData.lista[0].lotes.length > 0 && 
                         mapData.lista[0].lotes[0].coordinates && mapData.lista[0].lotes[0].coordinates.length > 1) {
                  console.log('Usando coordenadas do primeiro lote disponível');
                  mapData.center = mapData.lista[0].lotes[0].coordinates[0];
                }
                else {
                  console.log('Usando coordenadas padrão para Brasil');
                  mapData.center = [-15.77972, -47.92972]; // Padrão para Brasília, Brasil
                }
              } else {
                console.log('Nenhuma quadra encontrada, usando coordenadas padrão');
                mapData.center = [-15.77972, -47.92972]; // Padrão para Brasília, Brasil
              }
            }
            
            // Definir um nível de zoom adequado se não foi fornecido
            if (!mapData.zoom) {
              mapData.zoom = 18; // Zoom padrão para visualizar detalhes dos lotes
            }

            setListaDeMarcacoes(mapData);
            setTiposInadimplencia(mapData.tiposInadimplencia || []);
            setListaAreas(mapData.others);
            setMapaInfo({
              nome: obra,
              empresa: empresaMae
            });
            setCarregandoListaDeMarcacoes(false);
          } else {
            // Dados não encontrados
            console.error('Resposta da API indica dados não encontrados');
            setError('Mapa não encontrado ou sem permissão de acesso');
            setCarregandoListaDeMarcacoes(false);
          }
        } catch (error: any) {
          console.error('Erro ao carregar dados do mapa com parâmetros diretos:', error);
          setError('Erro ao carregar o mapa. Por favor, verifique o link ou tente novamente mais tarde.');
          setCarregandoListaDeMarcacoes(false);
        }
      } else {
        // Nenhum parâmetro válido encontrado
        console.error('Parâmetros incompletos na URL');
        setError('Parâmetros incompletos. Verifique o link.');
        setCarregandoListaDeMarcacoes(false);
      }
    }
    
    loadMapData();
    
    return () => {
      // Limpa o intervalo de atualização quando o componente é desmontado
      if (refreshData) {
        clearInterval(refreshData);
      }
    };
  }, [empresaMae, obra, token]);

  // Função para remover a atualização periódica de dados
  const removeRefreshData = () => {
    setRefreshData(null);
  };

  // Função para filtrar apenas unidades disponíveis
  const filtrarApenasDisponiveis = (dados: any) => {
    if (!dados || !mostrarApenasDisponiveis) {
      return dados;
    }

    // Criar uma cópia dos dados para não modificar o original
    const dadosFiltrados = { ...dados };
    
    if (dadosFiltrados.lista && Array.isArray(dadosFiltrados.lista)) {
      dadosFiltrados.lista = dadosFiltrados.lista.map((quadra: any) => {
        if (quadra.lotes && Array.isArray(quadra.lotes)) {
          return {
            ...quadra,
            lotes: quadra.lotes.filter((lote: any) => {
              // Verificar se o lote está disponível (status === 0)
              // Pode estar em lote.dadosUau.status ou lote.objeto.status
              const status = lote.dadosUau?.status ?? lote.objeto?.status ?? lote.status;
              return status === 0;
            })
          };
        }
        return quadra;
      }).filter((quadra: any) => quadra.lotes && quadra.lotes.length > 0); // Remover quadras sem lotes disponíveis
    }

    return dadosFiltrados;
  };

  // Função para alternar entre tela cheia e normal
  const toggleFullscreen = () => {
    if (isFullscreen) {
      handle.exit();
    } else {
      handle.enter();
    }
    setIsFullscreen(!isFullscreen);
  };
  
  // Estado para controlar a rotação do mapa (mesmo padrão do projeto original)
  const [getRotate, setRotate] = useState<[boolean, number]>([false, 0]);
  
  // Estado para controlar o tamanho dos marcadores baseado no zoom
  // Default: [16, 12, 6, 0.7] como no original
  const [getMarkersSize, setMarkersSize] = useState<[number, number, number, number]>([16, 12, 6, 0.7]);
  
  // Função para aplicar rotação ao mapa
  const handleRotation = (angle: number) => {
    // Usar o mesmo padrão do projeto original: [true, angulo]
    setRotate([true, angle]);
  };

  // Função para lidar com a mudança de estado da tela cheia
  const handleFullscreenChange = (state: boolean) => {
    setIsFullscreen(state);
  };

  // Opções para a animação de carregamento
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: LoadingAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  return (
    <div className="public-map-view" style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'absolute',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0
    }}>
      <FullScreen handle={handle} onChange={handleFullscreenChange}>
        {carregandoListaDeMarcacoes ? (
          <div className="loading-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: '100%'
          }}>
            <Lottie options={defaultOptions} height={200} width={200} />
            <p>Carregando mapa...</p>
          </div>
        ) : error ? (
          <Card className="error-card" style={{
            margin: '2rem auto',
            maxWidth: '500px'
          }}>
            <h3>Não foi possível carregar o mapa</h3>
            <p>{error}</p>
          </Card>
        ) : (
          <div 
            className="map-container" 
            style={{
              width: '100%',
              height: '100vh',
              position: 'relative',
              overflow: 'hidden',
              zIndex: 1
            }}
            onClick={() => console.log('Map container clicked')}
          >
            {mapaInfo && (
              <div className="map-info" style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                zIndex: 990, // Reduzir o z-index para ficar abaixo dos controles do Leaflet
                backgroundColor: 'rgba(255,255,255,0.8)',
                padding: '10px',
                borderRadius: '4px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}>
                <h2 style={{ margin: '0 0 5px 0' }}>{mapaInfo.nome}</h2>
                <p style={{ margin: '0' }}>{mapaInfo.empresa}</p>
                <Button
                  icon={isFullscreen ? "minimize" : "maximize"}
                  onClick={toggleFullscreen}
                  minimal
                  small
                />
              </div>
            )}
            <div className="map-wrapper" style={{ height: '100%', width: '100%' }}>
              {/* Seletores de visão do mapa simplificados */}
              <div className="view-selectors" style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 990, // Reduzir o z-index para ficar abaixo dos controles do Leaflet
                display: 'flex',
                backgroundColor: 'white',
                borderRadius: '4px',
                overflow: 'hidden',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                pointerEvents: 'auto' // Garantir que os cliques funcionem apenas nos botões
              }}>
                <Button
                  icon="path-search"
                  minimal={visaoMapa !== 0}
                  small
                  onClick={() => setVisaoMapa(0)}
                  style={{
                    backgroundColor: visaoMapa === 0 ? '#99acff' : 'white',
                    color: visaoMapa === 0 ? 'white' : 'inherit',
                    borderRadius: 0,
                    padding: '5px 10px'
                  }}
                />
                <Button
                  icon="dollar"
                  minimal={visaoMapa !== 1}
                  small
                  onClick={() => setVisaoMapa(1)}
                  style={{
                    backgroundColor: visaoMapa === 1 ? '#99acff' : 'white',
                    color: visaoMapa === 1 ? 'white' : 'inherit',
                    borderRadius: 0,
                    padding: '5px 10px'
                  }}
                />
                <Button
                  icon="double-caret-horizontal"
                  minimal={visaoMapa !== 2}
                  small
                  onClick={() => setVisaoMapa(2)}
                  style={{
                    backgroundColor: visaoMapa === 2 ? '#99acff' : 'white',
                    color: visaoMapa === 2 ? 'white' : 'inherit',
                    borderRadius: 0,
                    padding: '5px 10px'
                  }}
                />
                <Button
                  icon="comparison"
                  minimal={visaoMapa !== 3}
                  small
                  onClick={() => setVisaoMapa(3)}
                  style={{
                    backgroundColor: visaoMapa === 3 ? '#99acff' : 'white',
                    color: visaoMapa === 3 ? 'white' : 'inherit',
                    borderRadius: 0,
                    padding: '5px 10px'
                  }}
                />
              </div>
              
              {/* Filtro de unidades disponíveis */}
              <div className="available-filter-control" style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                zIndex: 2001,
                backgroundColor: 'white',
                borderRadius: '4px',
                padding: '8px 12px',
                boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <input
                  type="checkbox"
                  id="mostrarApenasDisponiveis"
                  checked={mostrarApenasDisponiveis}
                  onChange={(e) => setMostrarApenasDisponiveis(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label 
                  htmlFor="mostrarApenasDisponiveis" 
                  style={{ 
                    cursor: 'pointer', 
                    fontSize: '12px', 
                    fontWeight: 600,
                    color: '#696969',
                    userSelect: 'none'
                  }}
                >
                  Apenas Disponíveis
                </label>
              </div>
              
              {/* Controles de visualização (GPS/Satélite) */}
              <div className="view-type-control" style={{
                position: 'absolute',
                bottom: '10px',
                right: '10px',
                zIndex: 2001,
                backgroundColor: 'white',
                borderRadius: '4px',
                padding: '8px',
                boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center'
              }}>
                <div 
                  onClick={() => setTileLayer(0)}
                  style={{
                    background: tileLayer === 0 ? "#f3f3f3" : "unset",
                    cursor: "pointer",
                    borderRadius: "5px",
                    color: "#696969",
                    fontWeight: 600,
                    height: "60px",
                    width: "50px",
                    padding: "5px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: tileLayer === 0 ? "2px solid #99acff" : "1px solid #ddd"
                  }}
                >
                  <div style={{
                    height: "35px",
                    borderRadius: "5px",
                    boxShadow: "0 0 5px gray",
                    width: "35px",
                    backgroundImage: `url(${FundoOpenStreet})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "105px",
                    backgroundPositionY: "-3px",
                    backgroundPositionX: "-7px"
                  }}></div>
                  <span style={{ fontSize: '10px', textAlign: 'center' }}>GPS</span>
                </div>
                
                <div 
                  onClick={() => setTileLayer(1)}
                  style={{
                    background: tileLayer === 1 ? "#f3f3f3" : "unset",
                    cursor: "pointer",
                    borderRadius: "5px",
                    color: "#696969",
                    fontWeight: 600,
                    height: "60px",
                    width: "50px",
                    padding: "5px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: tileLayer === 1 ? "2px solid #99acff" : "1px solid #ddd"
                  }}
                >
                  <div style={{
                    height: "35px",
                    borderRadius: "5px",
                    boxShadow: "0 0 5px gray",
                    width: "35px",
                    backgroundImage: `url(${FundoSatelite})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "105px",
                    backgroundPositionY: "-3px",
                    backgroundPositionX: "-7px"
                  }}></div>
                  <span style={{ fontSize: '10px', textAlign: 'center' }}>Satélite</span>
                </div>
              </div>
              
              {/* Legenda */}
              <Opcoes
                getExibirLegenda={exibirLegenda}
                setExibirLegenda={setExibirLegenda}
                getLegendaOption={legendaOption}
                setLegendaOption={setLegendaOption}
                getTileLayer={tileLayer}
                setTileLayer={setTileLayer}
                getListaPickerQuadra={[]}
                setConsumer={() => {}}
                optionsToChoose={optionsToChoose}
                Icon={Button}
                tiposInadimplencia={tiposInadimplencia}
                getListaFaixa={[]}
                getListaCategoria={[]}
                Calcularcenario={() => {}}
                columnsData={[
                  { name: "Status", width: "31%" },
                  { name: "Área(m²)", width: "calc(22% - 0.5px)" },
                  { name: "Qtd", width: "15%" },
                  { name: "Valor(R$)", width: "32%" }
                ]}
                visao={visaoMapa}
                statusData={[
                  { id: 0, name: "Disponível", color: "green" },
                  { id: 1, name: "Vendido", color: "red" },
                  { id: 2, name: "Reservado", color: "yellow" },
                  { id: 3, name: "Proposta", color: "darkorange" },
                  { id: 4, name: "Quitado", color: "#7757A4" },
                  { id: 5, name: "Escriturado", color: "gray" },
                  { id: 6, name: "Em Venda", color: "black" },
                  { id: 7, name: "Suspenso Venda", color: "darkcyan" },
                  { id: 8, name: "Fora de Venda", color: "#D7D5D2" },
                  { id: 9, name: "Em Acerto", color: "pink" },
                  { id: 10, name: "Dação", color: "darkblue" }
                ]}
                Image={{ FundoOpenStreet, FundoSatelite }}
              />
              
              {/* Controle de rotação do mapa original */}
              <div className="rotation-control" style={{
                position: 'absolute',
                top: '180px',
                right: '10px',
                zIndex: 2001, 
                backgroundColor: 'white',
                borderRadius: '4px',
                padding: '5px',
                boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px'
              }}>
                <Button
                  icon="refresh"
                  minimal
                  onClick={() => handleRotation(0)}
                  title="Resetar rotação"
                />
                <Button
                  icon="arrow-left"
                  minimal
                  onClick={() => handleRotation(-45)}
                  title="Rotacionar 45° anti-horário"
                />
                <Button
                  icon="arrow-right"
                  minimal
                  onClick={() => handleRotation(45)}
                  title="Rotacionar 45° horário"
                />
              </div>

              <Map
                getRefreshData={refreshData}
                RefreshData={() => {}}
                RemoveRefreshData={removeRefreshData}
                visao={visaoMapa}
                refresh={mapRefreshed}
                setRefresh={setMapRefreshed}
                polygons={filtrarApenasDisponiveis(listaDeMarcacoes)}
                setPolygons={setListaDeMarcacoes}
                tiposInadimplencia={tiposInadimplencia}
                areas={listaAreas}
                points={[]}
                getCenter={listaDeMarcacoes?.center || [-15.77972, -47.92972]}
                getMarkersSize={getMarkersSize}
                getRuaSize={[100, 80, 10, 0]}
                getLocalData={filtrarApenasDisponiveis(listaDeMarcacoes)}
                getConsumer={[false, [0, 0]]}
                getTileLayer={tileLayer}
                MyComponent={() => {
                  // Componente que monitora o zoom do mapa
                  function ZoomHandler() {
                    const map = useMapEvents({
                      zoomend: () => {
                        const zoom = map.getZoom();
                        
                        // Ajustar o tamanho dos marcadores com base no zoom atual
                        if (zoom >= 21) {
                          // Zoom extremo
                          setMarkersSize([32, 24, 12, 1]);
                        } else if (zoom >= 20) {
                          setMarkersSize([28, 22, 11, 1]);
                        } else if (zoom >= 19) {
                          setMarkersSize([26, 20, 10, 1]);
                        } else if (zoom >= 18) {
                          // Zoom próximo
                          setMarkersSize([24, 18, 9, 1]);
                        } else if (zoom === 17) {
                          // Zoom intermediário alto
                          setMarkersSize([22, 16, 8, 0.95]);
                        } else if (zoom === 16) {
                          // Zoom intermediário (padrão)
                          setMarkersSize([18, 14, 7, 0.9]);
                        } else if (zoom === 15) {
                          // Zoom intermediário baixo
                          setMarkersSize([16, 12, 6, 0.85]);
                        } else {
                          // Zoom distante
                          setMarkersSize([14, 10, 5, 0.8]);
                        }
                      }
                    });
                    
                    return null;
                  }
                  
                  return <ZoomHandler />;
                }}
                setConsumer={() => {}}
                setRotate={setRotate}
                getRotate={getRotate}
                componentRef={React.createRef()}
                localRefresh={true}
                setLocalRefresh={() => {}}
              />
            </div>
          </div>
        )}
      </FullScreen>
    </div>
  );
};

export default PublicMapView;
